/**
 * Spot WebSocket API
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { WebsocketAPIBase, WebsocketApiResponse, WebsocketSendMsgOptions } from '@binance/common';
import type {
    ExchangeInfoResponse,
    ExecutionRulesResponse,
    PingResponse,
    TimeResponse,
} from '../types';

/**
 * GeneralApi - interface
 *
 * @interface GeneralApi
 */
export interface GeneralApiInterface {
    /**
     * Query current exchange trading rules, rate limits, and symbol
     * information.
     *
     * Weight(IP): 20
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Notes:**
     * If the value provided to `symbol` or `symbols` do not exist, the endpoint will throw an error saying the symbol is invalid.
     * All parameters are optional.
     * Only one of `symbol`, `symbols`, `permissions` parameters can be specified.
     * Without parameters, `exchangeInfo` displays all symbols with `["SPOT", "MARGIN", "LEVERAGED"]` permissions.
     * In order to list *all* active symbols on the exchange, you need to explicitly request all permissions.
     * `permissions` accepts either a list of permissions, or a single permission name. E.g. `"SPOT"`.
     *
     **Examples of Symbol Permissions Interpretation from the Response:**
     *
     * `[["A","B"]]` means you may place an order if your account has either permission "A" **or** permission "B".
     * `[["A"],["B"]]` means you can place an order if your account has permission "A" **and** permission "B".
     * `[["A"],["B","C"]]` means you can place an order if your account has permission "A" **and** permission "B" or permission "C". (Inclusive or is applied here, not exclusive or, so your account may have both permission "B" and permission "C".)
     *
     * @summary Exchange information
     * @param {ExchangeInfoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<ExchangeInfoResponse>}
     * @memberof GeneralApiInterface
     */
    exchangeInfo(
        requestParameters?: ExchangeInfoRequest
    ): Promise<WebsocketApiResponse<ExchangeInfoResponse>>;

    /**
     * Query execution rules for symbols.
     *
     * Weight: Parameter | Weight
     * --- | ---
     * `symbol` | 2
     * `symbols` | 2 for each `symbol`, capped at a max of 40
     * `symbolStatus` | 40
     * None | 40
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Note:** No combination of multiple parameters is allowed.
     *
     * @summary Query Execution Rules
     * @param {ExecutionRulesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<ExecutionRulesResponse>}
     * @memberof GeneralApiInterface
     */
    executionRules(
        requestParameters?: ExecutionRulesRequest
    ): Promise<WebsocketApiResponse<ExecutionRulesResponse>>;

    /**
     * Test connectivity to the WebSocket API.
     *
     * Note: You can use regular WebSocket ping frames to test connectivity as well, WebSocket API will respond with pong frames as soon as possible. ping request along with time is a safe way to test request-response handling in your application.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test connectivity
     * @param {PingRequest} requestParameters Request parameters.
     *
     * @returns {Promise<PingResponse>}
     * @memberof GeneralApiInterface
     */
    ping(requestParameters?: PingRequest): Promise<WebsocketApiResponse<PingResponse>>;

    /**
     * Test connectivity to the WebSocket API and get the current server time.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Check server time
     * @param {TimeRequest} requestParameters Request parameters.
     *
     * @returns {Promise<TimeResponse>}
     * @memberof GeneralApiInterface
     */
    time(requestParameters?: TimeRequest): Promise<WebsocketApiResponse<TimeResponse>>;
}

/**
 * Request parameters for exchangeInfo operation in GeneralApi.
 * @interface ExchangeInfoRequest
 */
export interface ExchangeInfoRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof GeneralApiExchangeInfo
     */
    readonly id?: string;

    /**
     * Describe a single symbol
     * @type {string}
     * @memberof GeneralApiExchangeInfo
     */
    readonly symbol?: string;

    /**
     * Describe multiple symbols
     * @type {Array<string>}
     * @memberof GeneralApiExchangeInfo
     */
    readonly symbols?: Array<string>;

    /**
     * Filter symbols by permissions
     * @type {Array<string>}
     * @memberof GeneralApiExchangeInfo
     */
    readonly permissions?: Array<string>;

    /**
     * Controls whether the content of the `permissionSets` field is populated or not. Defaults to `true`.
     * @type {boolean}
     * @memberof GeneralApiExchangeInfo
     */
    readonly showPermissionSets?: boolean;

    /**
     * Filters for symbols that have this `tradingStatus`. Valid values: `TRADING`, `HALT`, `BREAK`. Cannot be used in combination with `symbol` or `symbols`.
     * @type {'TRADING' | 'HALT' | 'BREAK'}
     * @memberof GeneralApiExchangeInfo
     */
    readonly symbolStatus?: ExchangeInfoSymbolStatusEnum;
}

/**
 * Request parameters for executionRules operation in GeneralApi.
 * @interface ExecutionRulesRequest
 */
export interface ExecutionRulesRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof GeneralApiExecutionRules
     */
    readonly id?: string;

    /**
     * Query for specified symbol.
     * @type {string}
     * @memberof GeneralApiExecutionRules
     */
    readonly symbol?: string;

    /**
     * Query for multiple symbols.
     * @type {Array<string>}
     * @memberof GeneralApiExecutionRules
     */
    readonly symbols?: Array<string>;

    /**
     * Query for all symbols with the specified status. Supported values: `TRADING`, `HALT`, `BREAK`
     * @type {'TRADING' | 'HALT' | 'BREAK'}
     * @memberof GeneralApiExecutionRules
     */
    readonly symbolStatus?: ExecutionRulesSymbolStatusEnum;
}

/**
 * Request parameters for ping operation in GeneralApi.
 * @interface PingRequest
 */
export interface PingRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof GeneralApiPing
     */
    readonly id?: string;
}

/**
 * Request parameters for time operation in GeneralApi.
 * @interface TimeRequest
 */
export interface TimeRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof GeneralApiTime
     */
    readonly id?: string;
}

/**
 * GeneralApi - object-oriented interface
 * @class GeneralApi
 * @extends {WebsocketAPIBase}
 */
export class GeneralApi implements GeneralApiInterface {
    private readonly websocketBase: WebsocketAPIBase;

    constructor(websocketBase: WebsocketAPIBase) {
        this.websocketBase = websocketBase;
    }

    /**
     * Query current exchange trading rules, rate limits, and symbol
     * information.
     *
     * Weight(IP): 20
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Notes:**
     * If the value provided to `symbol` or `symbols` do not exist, the endpoint will throw an error saying the symbol is invalid.
     * All parameters are optional.
     * Only one of `symbol`, `symbols`, `permissions` parameters can be specified.
     * Without parameters, `exchangeInfo` displays all symbols with `["SPOT", "MARGIN", "LEVERAGED"]` permissions.
     * In order to list *all* active symbols on the exchange, you need to explicitly request all permissions.
     * `permissions` accepts either a list of permissions, or a single permission name. E.g. `"SPOT"`.
     *
     **Examples of Symbol Permissions Interpretation from the Response:**
     *
     * `[["A","B"]]` means you may place an order if your account has either permission "A" **or** permission "B".
     * `[["A"],["B"]]` means you can place an order if your account has permission "A" **and** permission "B".
     * `[["A"],["B","C"]]` means you can place an order if your account has permission "A" **and** permission "B" or permission "C". (Inclusive or is applied here, not exclusive or, so your account may have both permission "B" and permission "C".)
     *
     * @summary Exchange information
     * @param {ExchangeInfoRequest} requestParameters Request parameters.
     * @returns {Promise<ExchangeInfoResponse>}
     * @memberof GeneralApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/general#exchange-info Binance API Documentation}
     */
    public exchangeInfo(
        requestParameters: ExchangeInfoRequest = {}
    ): Promise<WebsocketApiResponse<ExchangeInfoResponse>> {
        return this.websocketBase.sendMessage<ExchangeInfoResponse>(
            '/exchangeInfo'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: false, withApiKey: false }
        );
    }

    /**
     * Query execution rules for symbols.
     *
     * Weight: Parameter | Weight
     * --- | ---
     * `symbol` | 2
     * `symbols` | 2 for each `symbol`, capped at a max of 40
     * `symbolStatus` | 40
     * None | 40
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Note:** No combination of multiple parameters is allowed.
     *
     * @summary Query Execution Rules
     * @param {ExecutionRulesRequest} requestParameters Request parameters.
     * @returns {Promise<ExecutionRulesResponse>}
     * @memberof GeneralApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/general#execution-rules Binance API Documentation}
     */
    public executionRules(
        requestParameters: ExecutionRulesRequest = {}
    ): Promise<WebsocketApiResponse<ExecutionRulesResponse>> {
        return this.websocketBase.sendMessage<ExecutionRulesResponse>(
            '/executionRules'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: false, withApiKey: false }
        );
    }

    /**
     * Test connectivity to the WebSocket API.
     *
     * Note: You can use regular WebSocket ping frames to test connectivity as well, WebSocket API will respond with pong frames as soon as possible. ping request along with time is a safe way to test request-response handling in your application.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test connectivity
     * @param {PingRequest} requestParameters Request parameters.
     * @returns {Promise<PingResponse>}
     * @memberof GeneralApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/general#ping Binance API Documentation}
     */
    public ping(requestParameters: PingRequest = {}): Promise<WebsocketApiResponse<PingResponse>> {
        return this.websocketBase.sendMessage<PingResponse>(
            '/ping'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: false, withApiKey: false }
        );
    }

    /**
     * Test connectivity to the WebSocket API and get the current server time.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Check server time
     * @param {TimeRequest} requestParameters Request parameters.
     * @returns {Promise<TimeResponse>}
     * @memberof GeneralApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/general#time Binance API Documentation}
     */
    public time(requestParameters: TimeRequest = {}): Promise<WebsocketApiResponse<TimeResponse>> {
        return this.websocketBase.sendMessage<TimeResponse>(
            '/time'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: false, withApiKey: false }
        );
    }
}

export enum ExchangeInfoSymbolStatusEnum {
    TRADING = 'TRADING',
    HALT = 'HALT',
    BREAK = 'BREAK',
}

export enum ExecutionRulesSymbolStatusEnum {
    TRADING = 'TRADING',
    HALT = 'HALT',
    BREAK = 'BREAK',
}
